// AI Generated Component
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DirectNavigationService } from '@monsido/services/direct-navigation-service/direct-navigation.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'page-details-section-qa-spell',
    standalone: false,
    template: `
        <div class="d-flex align-items-center mb-2">
            <span class="fa-2x fas fa-fw fa-language mr-2 text-link mt-1" aria-hidden="true"></span>
            <div>
                <h2 class="font-weight-300 mb-0">
                    {{headline}}
                </h2>
                @let totalWords = (words?.total || 0) | monNumber;
                <span class="text-grey" translate [translate-args]="{$count: totalWords}" [translate-n]="totalWords" translate-plural="[[$count]] words found">
                    1 word found
                </span>
            </div>
        </div>
        <mon-tabs [options]="tabs"
                  [(status)]="activeTab"
                  (onStatus)="onStatus($event)"
                  (onSearch)="onSearch($event)"
                  [search]="search"
                  [minSearchLength]="1"
                  [showSearch]="true">
                  <csv-export-button [exports]="exports"></csv-export-button>
        </mon-tabs>
        <div class="card panel-monsido-card">
            <div class="card-body">
                <table-container
                        [collection]="words"
                        (onPageChange)="onPageChange($event)"
                        (onPerPageChange)="onPageSizeChange($event)"
                        [progress]="promise"
                        noResultMessage="{{ 'Good job! No issues were found' | translate }}">
                    <div class="row">
                        <div class="col-48 px-4">
                            <table class="table table-hover table-fixed table-vertial-align">
                                <thead>
                                <tr>
                                    <th class="col-sm-1 text-center" [attr.aria-label]="'Check all misspellings in this section' | translate">
                                        <table-select-item-all [(selectedItems)]="selected" [items]="words"></table-select-item-all>
                                    </th>
                                    <th>
                                        <table-dictionary-all
                                                [(selected)]="selected"
                                                (callback)="updatePage()"
                                                [showConfirm]="type === 'potential'"
                                                [showIgnore]="true"
                                                [pageId]="pageObj.id">
                                        </table-dictionary-all>
                                        <span translate *ngIf="selected.length === 0">Word</span>
                                    </th>
                                    <th class="col-sm-8 col-md-7 col-lg-12 text-right" translate>Lookup in Google</th>
                                    <th class="col-sm-8 text-right" *ngIf="showAutofix" translate>PageCorrect</th>
                                    <th class="col-sm-5 col-lg-4 text-right" translate>Language</th>
                                    <th class="col-sm-8 col-md-7 col-lg-5 text-right" *ngIf="type === 'potential'" translate>Confirm</th>
                                    <th class="col-sm-10 text-right"></th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr *ngFor="let word of words; trackBy: trackByWordId">
                                    <td class="text-center text-v-middle" [attr.aria-label]="'Check misspelling' | translate">
                                        <table-select-item [(selectedItems)]="selected" [item]="word"></table-select-item>
                                    </td>
                                    <td class="text-v-middle text-ellipsis">{{ word.word }}</td>
                                    <td class="text-v-middle text-right">
                                        <a class="btn cursor-pointer" monEventTrigger="actionTrigger" [monEventParams]="{action: 'open-in-google-btn'}" target="_blank" aria-describedby="linkOpensInNewTab" [href]="'https://www.google.com/search?q=' + word.word">
                                            <span class="fab fa-google" aria-hidden="true"></span>
                                            <span class="sr-only" translate>Google</span>
                                        </a>
                                    </td>
                                    <td class="text-v-middle text-right" *ngIf="showAutofix">
                                        <page-fix-icon (cb)="getPage()" type="spelling_error" [model]="word" />
                                    </td>
                                    <td class="text-v-middle text-right"> <language [language]="word.language"></language></td>
                                    <td class="text-v-middle text-right" *ngIf="type === 'potential'">
                                        <a class="cursor-pointer"
                                           monEventTrigger="actionTrigger"
                                           [monEventParams]="{action: 'confirm-misspelling-btn'}"
                                           monModalConfirm="{{ 'Are you sure you want to confirm this misspelling?' | translate }}"
                                           (monModalConfirmAction)="confirmMisspelling(word)">
                                           {{ 'Confirm misspelling' | translate }}
                                        </a>
                                    </td>
                                    <td class="text-v-middle text-right">
                                        <div class="d-flex flex-wrap align-items-center justify-content-end">
                                            <button class="btn btn-border-radius btn-secondary"
                                                    triggers="focus hover"
                                                    ngbTooltip="{{ 'Open misspelling page' | translate }}"
                                                    (click)="openIssueModal(word)"
                                                    monEventTrigger="actionTrigger"
                                                    [monEventParams]="{action: 'open-issue-view', params: {module: headline}}">
                                                <span class="fas fa-info-circle" aria-hidden="true"></span>
                                            </button>
                                            <mon-action-menu [monActionMenuItems]="word.dropdown" class="ml-1">
                                                <span translate>Action</span>
                                            </mon-action-menu>
                                            <mon-extension-visual-link-button class="ml-3"
                                                                          [page]="pageObj"
                                                                          [type]="'mspel'"
                                                                          [data]="word">
                                            </mon-extension-visual-link-button>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </table-container>
            </div>
        </div>
    `,
})
export class PageDetailsSectionQaSpellComponent extends BaseContainerComponentController implements OnInit {
    @Input() pageObj: any;
    @Input() type: string = 'confirmed';
    @Input() headline?: string;
    @Output() refreshPage = new EventEmitter<void>();

    private translateService = inject(TranslateService);
    private directNavigationService = inject(DirectNavigationService);
    private activeFeatureService = inject(ActiveFeatureService);
    private monModalService = inject(MonModalService);

    selected: any[] = [];
    words: any = {};
    showAutofix: boolean = false;
    tabs: any[] = [];
    override activeTab: string = 'all';
    override search: string = '';
    exports: any[] = [];
    promise: any;

    constructor () {
        super({ table: true });
    }

    ngOnInit (): void {
        this.showAutofix = this.activeFeatureService.isFeatureActive('page_fix_qa_misspelling') && this.type !== 'potential';
        this.selected = [];
        this.type = this.type || 'confirmed';
        this.search = '';
        this.headline = this.headline || this.translateService.getString('Misspellings');
        this.words = [];
        this.tabs = [
            {
                name: this.translateService.getString('All'),
                faIcon: 'fas fa-language',
                value: 'all',
            },
        ];

        this.spellingRepoService.getAllLanguages({ domainId: (this as any).domainId } as any).then((data: any[]) => {
            data.forEach((item: any) => {
                this.tabs.push({
                    name: this.pipeService.get('translateLanguage')(item.lang),
                    faIcon: 'fas fa-language',
                    value: item.lang,
                });
            });

            if (this.tabs.length > 0) {
                this.activeTab = this.tabs[0].value;
            }

            if (this.pageObj) {
                this.exports = [
                    {
                        name: this.type === 'confirmed'
                            ? this.translateService.getString('Confirmed misspellings')
                            : this.translateService.getString('Potential misspellings'),
                        data: {
                            category: this.type + '_misspelling',
                            category_ref: this.pageObj.id,
                        },
                    },
                ];
            }

            this.getPage();
        }).catch(() => {});

        this.monEventsService.addListener('reloadMisspelling', () => {
            this.getPage();
            this.refreshPage.emit();
        });
    }

    override getPage = (): void => {
        this.selected = [];
        const params: any = {
            page: this.page,
            page_size: this.pageSize,
            language: this.activeTab === 'all' ? null : this.activeTab,
            search: this.search,
            classification: this.type,
            page_fixed: false,
        };

        this.promise = this.spellingRepoService.getAllPageSpellingErrors(this.pageObj.id, params).then((data: any) => {
            this.words = data;
            this.updateDropdowns();
        }).catch(() => {});
    };

    updateDropdowns (): void {
        for (const word of this.words) {
            const dropdown = [
                {
                    label: this.translateService.getString('Add to dictionary'),
                    leftIcon: 'faBook',
                    action: () => this.addToDictionary(word),
                    eventParams: { action: 'add-to-dictionary-btn' },
                },
                {
                    label: this.translateService.getString('Add to dictionary for all languages'),
                    leftIcon: 'faAtlas',
                    action: () => this.addToAllDictionaries(word),
                    eventParams: { action: 'add-to-all-language-btn' },
                },
                {
                    label: this.translateService.getString('Ignore misspelling'),
                    leftIcon: 'faEyeSlash',
                    action: () => this.ignoreMisspelling(word),
                    eventParams: { action: 'ignore-misspelling-btn' },
                },
                {
                    label: this.translateService.getString('Mark as fixed'),
                    leftIcon: 'faCheck',
                    action: () => this.markMisspellingFixed(word),
                    eventParams: { action: 'mark-misspelling-fixed-btn' },
                },
            ];

            word.dropdown = dropdown.filter((option: any) => option.shouldShow !== false).map((item: any) => {
                return {
                    label: item.label,
                    leftIcon: item.leftIcon,
                    disabled: item.disabled,
                    action: () => {
                        this.monEventsService.run('actionTrigger', item.eventParams);
                        item.action();
                    },
                };
            });
        }
    }

    promptConfirmation (message: string, callback: () => void): void {
        this.monModalService.confirm(message)
            .then(callback)
            .catch(() => {});
    }

    confirmMisspelling (word: any): void {
        this.spellingRepoService.confirmWord(word.id, {} as any).then(() => {
            this.updatePage();
        }).catch(() => {});
    }

    markMisspellingFixed (word: any): void {
        const message = this.translateService.getString('Are you sure you want to mark this misspelling as fixed?');
        this.promptConfirmation(message, () => {
            this.spellingRepoService.destroyPageSpellingError(this.pageObj.id, word.id).then(() => {
                this.updatePage();
            }).catch(() => {});
        });
    }

    ignoreMisspelling (word: any): void {
        const message = this.translateService.getString('Are you sure you want to ignore this misspelling?');
        this.promptConfirmation(message, () => {
            this.spellingRepoService.updatePageIgnoreSpellingError(this.pageObj.id, word.id).then(() => {
                this.updatePage();
            }).catch(() => {});
        });
    }

    updatePage (): void {
        this.refreshPage.emit();
        this.getPage();
    }

    addToDictionary (word: any): void {
        const message = this.translateService.getString('Are you sure you want to add the word <strong>[[word]]</strong> to the dictionary?', { word: word.word });
        this.promptConfirmation(message, () => {
            const params = {
                word: word.word,
                language: word.language,
            };

            this.spellingRepoService.addToDictionary(params).then(() => {
                this.updatePage();
            }).catch(() => {});
        });
    }

    addToAllDictionaries (word: any): void {
        const message = this.translateService.getString('Are you sure you want to add the word <strong>[[word]]</strong> to the dictionary for all languages?',
            { word: word.word });
        this.promptConfirmation(message, () => {
            const params = {
                word: word.word,
            };
            this.spellingRepoService.addToDictionary(params).then(() => {
                this.updatePage();
            }).catch(() => {});
        });
    }

    openIssueModal (issue: any): void {
        this.directNavigationService.navigateToIssueOverlay({
            issueOverlayIssueId: issue.id,
            issueOverlayIssueType: this.type === 'potential' ? 'potentialMisspellings' : 'misspellings',
            issueOverlayPageId: this.pageObj?.id,
        });
    }

    trackByWordId (index: number, word: any): any {
        return word.id;
    }
}
